import React, { useState } from "react";
import { EnvelopeSimple, Phone, MapPin, WhatsappLogo, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { submitContact } from "@/lib/api";

export default function ContactPage({ config }) {
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", subject: "", message: "",
  });

  const phone = config?.whatsapp_phone || "233592208031";
  const email = config?.contact_email || "info@obrasafe.org";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitContact(form);
      setSent(true);
      toast.success("Message received. We'll reply within 24 hours.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      toast.error("Could not send message. Try WhatsApp instead.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-[#0a0a0a] text-white min-h-screen">
      <div className="border-b border-white/10 grain relative">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 relative">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#FFC107] mb-4">// Contact</p>
          <h1 className="font-display text-5xl sm:text-6xl md:text-7xl font-black uppercase tracking-tight leading-[0.95]">
            Reach the team.
          </h1>
          <p className="mt-6 text-lg text-zinc-400 max-w-2xl">
            Got a fleet, a security question, or a bulk order? Send us a message — or skip the queue and ping us on WhatsApp.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5 space-y-6">
          <div className="border border-white/10 p-6 bg-[#141414]">
            <MapPin size={28} weight="duotone" className="text-[#FFC107] mb-3" />
            <h3 className="font-display font-bold uppercase tracking-wider mb-1">Showroom</h3>
            <a
              href="https://maps.app.goo.gl/p1RMdsKmfeaXdfd8A"
              target="_blank"
              rel="noreferrer"
              className="text-zinc-400 hover:text-[#FFC107] text-sm leading-relaxed"
            >
              Lionsgate Residence<br />Accra, Ghana
            </a>
          </div>
          <div className="border border-white/10 p-6 bg-[#141414]">
            <Phone size={28} weight="duotone" className="text-[#FFC107] mb-3" />
            <h3 className="font-display font-bold uppercase tracking-wider mb-1">Hotline</h3>
            <a href={`tel:+${phone}`} className="text-zinc-300 hover:text-[#FFC107]">+{phone}</a>
            <p className="text-xs text-zinc-500 mt-1">Mon–Sat, 8:00 – 19:00 GMT</p>
          </div>
          <div className="border border-white/10 p-6 bg-[#141414]">
            <EnvelopeSimple size={28} weight="duotone" className="text-[#FFC107] mb-3" />
            <h3 className="font-display font-bold uppercase tracking-wider mb-1">Email</h3>
            <a href={`mailto:${email}`} className="text-zinc-300 hover:text-[#FFC107]">{email}</a>
          </div>
          <a
            href={`https://wa.me/${phone}?text=${encodeURIComponent("Hi ObraSafe, I would like to enquire about…")}`}
            target="_blank"
            rel="noreferrer"
            data-testid="contact-whatsapp-cta"
            className="block bg-[#25D366] hover:bg-[#1DA851] text-white p-6 transition-colors"
          >
            <WhatsappLogo size={28} weight="fill" className="mb-3" />
            <h3 className="font-display font-black uppercase tracking-wider mb-1">Chat on WhatsApp</h3>
            <p className="text-sm opacity-80">Fastest way to get a quote — usually under 5 mins.</p>
          </a>
        </div>

        <div className="md:col-span-7">
          <div className="border border-white/10 bg-[#141414] p-6 md:p-8">
            {sent ? (
              <div className="text-center py-8" data-testid="contact-sent">
                <CheckCircle size={48} weight="fill" className="text-[#FFC107] mx-auto mb-4" />
                <h3 className="font-display text-2xl font-black uppercase mb-2">Message received</h3>
                <p className="text-zinc-400 mb-6">We&apos;ll get back to you within 24 hours.</p>
                <button
                  data-testid="contact-send-another"
                  onClick={() => setSent(false)}
                  className="h-11 px-6 border border-white/20 hover:border-[#FFC107] hover:text-[#FFC107] uppercase tracking-wider text-xs font-bold"
                >
                  Send another
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <h2 className="font-display text-2xl font-black uppercase mb-2">Send a Message</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input data-testid="contact-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className="h-12 px-4 bg-[#0a0a0a] border border-white/15 focus:border-[#FFC107] outline-none text-sm" />
                  <input data-testid="contact-phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" className="h-12 px-4 bg-[#0a0a0a] border border-white/15 focus:border-[#FFC107] outline-none text-sm" />
                </div>
                <input data-testid="contact-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="w-full h-12 px-4 bg-[#0a0a0a] border border-white/15 focus:border-[#FFC107] outline-none text-sm" />
                <input data-testid="contact-subject" required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Subject" className="w-full h-12 px-4 bg-[#0a0a0a] border border-white/15 focus:border-[#FFC107] outline-none text-sm" />
                <textarea data-testid="contact-message" required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell us what you need…" className="w-full p-4 bg-[#0a0a0a] border border-white/15 focus:border-[#FFC107] outline-none text-sm" />
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit"
                  className="w-full h-12 bg-[#FFC107] hover:bg-[#FFB300] text-black font-black uppercase tracking-wider text-sm disabled:opacity-50"
                >
                  {submitting ? "Sending…" : "Send message"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
